/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
/* eslint-disable import/no-cycle */
import moment from 'moment';
import SegmentService from '@/services/segment';
import SurveyService from '@/services/survey/';
import DistributionService from '@/services/distribution/';
import MetadataService from '@/services/metadata';
import CategoryService from '@/services/category';
import ErrorHandler from '@/tools/errorHandler';
import { clearUnixDate } from '@/utils/date';
import * as types from './mutationTypes';
import { getSegmentDefault } from './segmentState';

function mountSegmentToSave(segment, type, offset) {
  const requestData = {
    uuid: segment.uuid,
    name: segment.name,
    type,
    rules: {
      static: null,
      metadata: segment.rules.metadata,
    },
  };

  const staticRules = { ...segment.rules.static };
  requestData.rules.static = JSON.stringify(staticRules);

  if (requestData.name === 'Untitled') {
    const data = moment
      .unix(new Date().getTime() / 1000)
      .utcOffset(offset)
      .format('YYYY-MM-DD HH:mm:ss');
    requestData.name = `Filter_${data}`;
  }

  return requestData;
}

export default {
  initState({ commit }) {
    commit(types.SET_SEGMENT, getSegmentDefault());
    commit(types.SET_SEGMENTS, []);
    commit(types.SET_SURVEY_OPTIONS, []);
    commit(types.SET_METADATA_OPTIONS, []);
    commit(types.SET_DISTRIBUTION_OPTIONS, []);
    commit(types.SET_CATEGORY_OPTIONS, []);
  },
  clearState({ commit, dispatch }) {
    commit(types.CLEAR_SEGMENT);
    commit(types.CLEAR_SEGMENTS);
    commit(types.CLEAR_SURVEY_OPTIONS);
    commit(types.CLEAR_METADATA_OPTIONS);
    commit(types.CLEAR_DISTRIBUTION_OPTIONS);
    commit(types.CLEAR_CATEGORY_OPTIONS);
    dispatch('category/clearCategories', null, { root: true });
  },
  setSegmentsSuccess({ commit }, segments) {
    commit(types.SET_SEGMENTS, segments);
  },
  setSurveyOptionSuccess({ commit }, surveys) {
    commit(types.SET_SURVEY_OPTIONS, surveys);
  },
  setDistributionOptionSuccess({ commit }, distributions) {
    commit(types.SET_DISTRIBUTION_OPTIONS, distributions);
  },
  setMetadataOptionSuccess({ commit }, metadatas) {
    commit(types.SET_METADATA_OPTIONS, metadatas);
  },
  setCategoryOptionSuccess({ commit }, categories) {
    commit(types.SET_CATEGORY_OPTIONS, categories);
  },
  setStaticSegment({ commit }, staticSegment) {
    commit(types.SET_STATIC_SEGMENT, staticSegment);
  },
  async getAll({ rootState, dispatch }, pagination) {
    try {
      const response = await SegmentService.getAll(
        rootState.organization.organization.uuid,
        pagination,
      );
      if (response.data.data) {
        dispatch('setSegmentsSuccess', response.data.data);
      }
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async create({ rootState, dispatch, state }, type) {
    try {
      const segment = mountSegmentToSave(
        state.segment,
        type,
        rootState.profile.profile.primary_time_offset,
      );

      const response = await SegmentService.create(
        rootState.organization.organization.uuid,
        segment,
      );
      if (response.data.data) {
        dispatch('addSegment', response.data.data);
        dispatch('parseSegment', response.data.data);
      }
      return true;
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async update({ rootState, dispatch, state }, type) {
    try {
      const segment = mountSegmentToSave(
        state.segment,
        type,
        rootState.profile.profile.primary_time_offset,
      );
      const response = await SegmentService.update(
        rootState.organization.organization.uuid,
        segment,
      );
      if (response.data.data) dispatch('parseSegment', response.data.data);
      return true;
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async save({ state, dispatch }, type) {
    if (state.segment.uuid) {
      return dispatch('update', type);
    }
    return dispatch('create', type);
  },
  async delete({ rootState, dispatch }, segmentUUID) {
    try {
      await SegmentService.delete(rootState.organization.organization.uuid, segmentUUID);
      dispatch('removeSegment', segmentUUID);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllSurveys({ rootState, dispatch }, pagination) {
    try {
      const response = await SurveyService.getAll(
        rootState.organization.organization.uuid,
        pagination,
      );
      if (response.data.data.survey) {
        dispatch('setSurveyOptionSuccess', response.data.data.survey);
      }
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async fetchDistributionsByName({ rootState, dispatch }, { text }) {
    try {
      const response = await DistributionService.getByName(
        rootState.organization.organization.uuid,
        text,
      );

      dispatch('setDistributionOptionSuccess', response.data.data.distribution || []);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async fetchSurveysByName({ rootState, dispatch }, { text }) {
    try {
      const response = await SurveyService.getAll(
        rootState.organization.organization.uuid,
        {
          name: text,
          rowsPerPage: 50,
          page: 1,
        },
      );
      dispatch('setSurveyOptionSuccess', response.data.data.survey || []);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllDistributions({ rootState, dispatch }, { pagination }) {
    try {
      const response = await DistributionService.getAll(
        rootState.organization.organization.uuid,
        pagination,
      );
      if (response.data.data.distribution) {
        dispatch('setDistributionOptionSuccess', response.data.data.distribution);
      }
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllMetadatas({ rootState, dispatch }, pagination) {
    try {
      const response = await MetadataService.getAll(
        rootState.organization.organization.uuid,
        pagination,
      );
      if (response.data.data.metadata) {
        dispatch('setMetadataOptionSuccess', response.data.data.metadata);
      }
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllCategories({ rootState, dispatch }, pagination) {
    try {
      const response = await CategoryService.getAll(
        rootState.organization.organization.uuid,
        pagination,
      );
      if (response.data.data) {
        dispatch('setCategoryOptionSuccess', response.data.data);
      }
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  parseSegment({ commit }, segmentData) {
    const segment = {
      uuid: segmentData.uuid,
      name: segmentData.name,
      rules: {
        static: JSON.parse(segmentData.rules.static),
        metadata: segmentData.rules.metadata,
      },
    };
    if (segment.rules.static.surveys === null) {
      segment.rules.static.surveys = [];
    }
    if (segment.rules.static.distributions === null) {
      segment.rules.static.distributions = [];
    }
    if (segment.rules.static.sending_channel === null) {
      segment.rules.static.sending_channel = [];
    }
    if (segment.rules.static.answer_channel === null) {
      segment.rules.static.answer_channel = [];
    }
    if (segment.rules.static.categories === null) {
      segment.rules.static.categories = [];
    }
    commit(types.SET_SEGMENT, segment);
  },
  clearSegment({ commit }) {
    commit(types.SET_SEGMENT, getSegmentDefault());
  },
  setName({ commit }, name) {
    commit(types.SET_NAME, name);
  },
  setSurveys({ commit }, surveys) {
    commit(types.SET_SURVEYS, surveys);
  },
  setDistributions({ commit }, distributions) {
    commit(types.SET_DISTRIBUTIONS, distributions);
  },
  setSendingDate({ commit }, { starts_at, ends_at }) {
    if (starts_at === '') starts_at = null;
    if (ends_at === '') ends_at = null;
    commit(types.SET_SENDING_DATE, {
      starts_at: clearUnixDate(starts_at),
      ends_at: clearUnixDate(ends_at, true),
    });
  },
  setAnswerDate({ commit }, { starts_at, ends_at }) {
    if (starts_at === '') starts_at = null;
    if (ends_at === '') ends_at = null;
    commit(types.SET_ANSWER_DATE, {
      starts_at: clearUnixDate(starts_at),
      ends_at: clearUnixDate(ends_at, true),
    });
  },
  setSendingChannel({ commit }, channel) {
    commit(types.SET_SENDING_CHANNEL, channel);
  },
  setAnswerChannel({ commit }, channel) {
    commit(types.SET_ANSWER_CHANNEL, channel);
  },
  setAnswerStatus({ commit }, status) {
    commit(types.SET_ANSWER_STATUS, status);
  },
  setSendStatus({ commit }, status) {
    commit(types.SET_SEND_STATUS, status);
  },
  setIdentification({ commit }, identification) {
    commit(types.SET_IDENTIFICATION, identification);
  },
  setHealthScore({ commit }, score) {
    commit(types.SET_HEALTH_SCORE, score);
  },
  setCategories({ commit }, categories) {
    commit(types.SET_CATEGORIES, categories);
  },
  setMetadata({ commit }, metadata) {
    commit(types.SET_METADATA, metadata);
  },
  addSegment({ commit }, segment) {
    commit(types.ADD_SEGMENT, segment);
  },
  removeSegment({ commit }, uuid) {
    commit(types.REMOVE_SEGMENT, uuid);
  },
};
